// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import {
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
  captureConsoleIntegration,
  contextLinesIntegration,
  httpClientIntegration,
  extraErrorDataIntegration,
} from '@sentry/react';
import http from 'api/http';
import { getTokenInfo } from 'helpers/getTokenInfo';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { userStore } from 'stores/userStore';

const IGNORE_MSG: (string | RegExp)[] = [
  //MUI
  /MUI X: /i,
  /Warning: /i,
  //cancel http request
  'canceled',
];

if (process.env.NODE_ENV === 'production') {
  init({
    dsn: import.meta.env.VITE_SENTRY_DSN || 'https://d1822fffa28f4cc78f0c0fd3ca6ad10d@o449774.ingest.sentry.io/5901745',
    tracesSampleRate: 1,
    normalizeDepth: 10,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      "t is not a function. (In 't()', 't' is undefined)", // 3rd party lib error
      /^Illegal invocation/,
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_DEPLOYMENT_ENV || 'development',
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      replayIntegration(),
      captureConsoleIntegration({
        levels: ['error'],
      }),
      contextLinesIntegration(),
      httpClientIntegration({
        // failedRequestStatusCodes: [400, [401, 599]],
        failedRequestTargets: [/^https:\/\/datahub/],
      }),
      extraErrorDataIntegration(),
    ],
    beforeSend(event, hint) {
      const msg = event.message || event.exception?.values?.[0]?.value || '';

      const shouldIgnore = IGNORE_MSG.some(p => (typeof p === 'string' ? msg.includes(p) : p.test(msg)));

      if (shouldIgnore) return null;

      const userInfo = userStore.getState().user;
      const token = http.getAuthToken();

      if (userInfo && token) {
        const tokenInfo = getTokenInfo(token);
        event.user = {
          id: userInfo?._dto?.id,
          email: userInfo?._dto?.email,
          fullName: userInfo?._dto?.fullName,
          currentlyImpersonatingEmail: userInfo?._dto?.currentlyImpersonatingEmail,
          currentlyImpersonatingUserId: userInfo?._dto?.currentlyImpersonatingUserId,
          issuedAt: tokenInfo.issuedAt,
          expiredAt: tokenInfo.expiresAt,
          timeToExpiry: tokenInfo.timeToExpiry,
        };
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
        return null;
      }
      return breadcrumb;
    },
  });
}
